import {DOCUMENT} from '@angular/common';
import {Inject, Injectable} from '@angular/core';
import {Meta, Title} from '@angular/platform-browser';
import {BehaviorSubject, Observable} from 'rxjs';

import {
  APP_INTERNAL_QUERY_PARAMS,
  BOARD_ROUTE_SEO_CONFIG,
  CANONICAL_HOME_PATHS,
  CANONICAL_LIST_PATH_MAP,
  CANONICAL_URL_BASE,
  DEFAULT_BOARD_SEO,
  DEFAULT_CANONICAL_LIST_TYPE,
  DIALECT_REGION_NATIONWIDE,
  HOME_SEO,
  OG_URL_BASE,
  RouteSeoItem,
  ROUTE_SEO_CONFIG,
  SITE_BRAND_NAME
} from './route-seo-config';

type ParsedRouteUrl = {
  path: string;
  queryParams: URLSearchParams;
};

@Injectable({
  providedIn: 'root'
})
export class SeoService {

  private readonly pageNameSubject = new BehaviorSubject<string>(SITE_BRAND_NAME);
  /** 현재 라우트의 H1/제목용 페이지명. 셸(AppComponent)의 시각 숨김 H1이 구독한다. */
  public readonly currentPageName$: Observable<string> = this.pageNameSubject.asObservable();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private title: Title,
    private meta: Meta,
  ) {
  }

  public applyRouteSeo(rawPath: string): void {
    const routeUrl = this.parseRouteUrl(rawPath);
    const seo = this.resolveRouteSeo(routeUrl.path);

    this.applySeo(routeUrl, seo);
  }

  public applyDynamicSeo(rawPath: string, seo: Pick<RouteSeoItem, 'pageName' | 'description'>): void {
    const routeUrl = this.parseRouteUrl(rawPath);

    this.applySeo(routeUrl, seo);
  }

  private applySeo(routeUrl: ParsedRouteUrl, seo: RouteSeoItem): void {
    const documentTitle = seo.isHome ? SITE_BRAND_NAME : `${seo.pageName} - ${SITE_BRAND_NAME}`;

    this.title.setTitle(documentTitle);
    this.pageNameSubject.next(seo.pageName);

    this.meta.updateTag({name: 'description', content: seo.description});
    this.meta.updateTag({property: 'og:title', content: documentTitle});
    this.meta.updateTag({property: 'og:description', content: seo.description});
    this.meta.updateTag({name: 'twitter:title', content: documentTitle});
    this.meta.updateTag({name: 'twitter:description', content: seo.description});
    this.meta.updateTag({property: 'og:url', content: this.buildMobileUrl(routeUrl)});
    this.updateCanonicalLink(this.buildCanonicalUrl(routeUrl));
  }

  private resolveRouteSeo(path: string): RouteSeoItem {
    const normalizedPath = this.normalizePath(path);
    const routeSeo = ROUTE_SEO_CONFIG[normalizedPath];
    if (routeSeo) {
      return routeSeo;
    }

    return this.resolveBoardRouteSeo(normalizedPath) ?? HOME_SEO;
  }

  private resolveBoardRouteSeo(path: string): RouteSeoItem | null {
    const match = path.match(/^\/board\/([^/]+)(?:\/[^/]+)?$/);
    if (!match) {
      return null;
    }

    return BOARD_ROUTE_SEO_CONFIG[match[1]] ?? DEFAULT_BOARD_SEO;
  }

  private normalizePath(rawPath: string): string {
    if (!rawPath) {
      return '/';
    }

    let normalizedPath = rawPath.trim();

    if (/^https?:\/\//.test(normalizedPath)) {
      try {
        const parsedUrl = new URL(normalizedPath);
        normalizedPath = parsedUrl.pathname + parsedUrl.hash;
      } catch (error) {
        console.warn('SeoService::normalizePath() URL parsing failed', {rawPath, error});
        return '/';
      }
    }

    const hashPathIndex = normalizedPath.indexOf('#/');
    if (hashPathIndex >= 0) {
      normalizedPath = normalizedPath.slice(hashPathIndex + 1);
    }

    if (normalizedPath.startsWith('#')) {
      normalizedPath = normalizedPath.slice(1);
    }

    normalizedPath = normalizedPath.split('?')[0].split('#')[0];

    if (!normalizedPath.startsWith('/')) {
      normalizedPath = `/${normalizedPath}`;
    }

    if (normalizedPath.length > 1 && normalizedPath.endsWith('/')) {
      normalizedPath = normalizedPath.slice(0, -1);
    }

    return normalizedPath || '/';
  }

  private parseRouteUrl(rawPath: string): ParsedRouteUrl {
    if (!rawPath) {
      return {
        path: '/',
        queryParams: new URLSearchParams(),
      };
    }

    let routePath = rawPath.trim();

    if (/^https?:\/\//.test(routePath)) {
      try {
        const parsedUrl = new URL(routePath);
        routePath = parsedUrl.hash.startsWith('#/')
          ? parsedUrl.hash.slice(1)
          : `${parsedUrl.pathname}${parsedUrl.search}`;
      } catch (error) {
        console.warn('SeoService::parseRouteUrl() URL parsing failed', {rawPath, error});
        return {
          path: '/',
          queryParams: new URLSearchParams(),
        };
      }
    }

    const hashPathIndex = routePath.indexOf('#/');
    if (hashPathIndex >= 0) {
      routePath = routePath.slice(hashPathIndex + 1);
    } else if (routePath.startsWith('#')) {
      routePath = routePath.slice(1);
    }

    const fragmentStart = routePath.indexOf('#');
    if (fragmentStart >= 0) {
      routePath = routePath.slice(0, fragmentStart);
    }

    const queryStart = routePath.indexOf('?');
    const path = queryStart >= 0 ? routePath.slice(0, queryStart) : routePath;
    const query = queryStart >= 0 ? routePath.slice(queryStart + 1) : '';

    return {
      path: this.normalizePath(path),
      queryParams: this.sanitizeQueryParams(new URLSearchParams(query)),
    };
  }

  private buildMobileUrl(routeUrl: ParsedRouteUrl): string {
    return this.joinUrl(OG_URL_BASE, routeUrl.path, routeUrl.queryParams);
  }

  private buildCanonicalUrl(routeUrl: ParsedRouteUrl): string {
    const queryParams = this.cloneQueryParams(routeUrl.queryParams);
    let canonicalPath = routeUrl.path;

    if (CANONICAL_HOME_PATHS.includes(routeUrl.path)) {
      return this.joinUrl(CANONICAL_URL_BASE, '/', new URLSearchParams());
    } else if (Object.prototype.hasOwnProperty.call(CANONICAL_LIST_PATH_MAP, routeUrl.path)) {
      const type = this.resolveListType(queryParams.get('l'));
      queryParams.delete('l');
      canonicalPath = `${CANONICAL_LIST_PATH_MAP[routeUrl.path]}/${type}`;
    } else if (routeUrl.path === '/dialect') {
      canonicalPath = this.buildDialectCanonicalPath(queryParams);
    } else if (routeUrl.path === '/dialect-movie') {
      queryParams.delete('l');
      canonicalPath = '/dialect/region';
    }

    return this.joinUrl(CANONICAL_URL_BASE, canonicalPath, queryParams);
  }

  private buildDialectCanonicalPath(queryParams: URLSearchParams): string {
    if (queryParams.has('map')) {
      const region = queryParams.get('map');
      queryParams.delete('map');
      queryParams.delete('l');

      if (region && region !== DIALECT_REGION_NATIONWIDE) {
        queryParams.set('region', region);
      }

      return '/dialect/region';
    }

    const type = this.resolveListType(queryParams.get('l'));
    queryParams.delete('l');
    return `/dialect-list/${type}`;
  }

  private resolveListType(rawType: string | null): string {
    const trimmed = rawType?.trim();
    if (!trimmed) {
      return DEFAULT_CANONICAL_LIST_TYPE;
    }
    return encodeURIComponent(trimmed);
  }

  private updateCanonicalLink(url: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel="canonical"]');

    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }

    link.href = url;
  }

  private joinUrl(baseUrl: string, path: string, queryParams: URLSearchParams): string {
    const normalizedPath = this.normalizePath(path);
    const query = queryParams.toString();
    return `${baseUrl}${normalizedPath}${query ? `?${query}` : ''}`;
  }

  private cloneQueryParams(queryParams: URLSearchParams): URLSearchParams {
    return new URLSearchParams(queryParams.toString());
  }

  private sanitizeQueryParams(queryParams: URLSearchParams): URLSearchParams {
    const sanitized = this.cloneQueryParams(queryParams);
    APP_INTERNAL_QUERY_PARAMS.forEach(param => sanitized.delete(param));
    return sanitized;
  }
}
